// idb.ts - IndexedDB helper
import { openDB, IDBPDatabase, DBSchema } from "idb";
import { InboundItem } from "../types/item";
import * as Sentry from "@sentry/react";
import { captureIndexDBError } from "./sentry-helpers";

// interface DBStructure {
//   assets: any;
//   inspectionPointsMapping: { tempId: string | number; realId: number };
//   images: { id: string; blob: Blob };
//   inboundItems: InboundItem[];
// }

// Add IndexedDB schema for InboundItems only
interface AppDB extends DBSchema {
  inboundItems: {
    key: number;
    value: InboundItem;
    indexes: { "by-status": number; "by-date": string };
  };
  assets: {
    key: any;
    value: any;
    indexes: { "by-status": number; "by-date": string };
  };
  inspectionPointsMapping: {
    key: number;
    value: { tempId: string | number; realId: number };
  };
  images: {
    key: string;
    value: { id: string; blob: Blob };
  };
  settings: {
    key: string;
    value: any;
  };
}

// ✅ Make version a constant for easier management
const DB_NAME = "asset-store";
const CURRENT_DB_VERSION = 9;

let dbInstance: IDBPDatabase<AppDB> | null = null;

/**
 * Get the existing database version without opening it
 */
async function getExistingDbVersion(): Promise<number | null> {
  return new Promise((resolve) => {
    const request = indexedDB.open(DB_NAME);
    request.onsuccess = () => {
      const version = request.result.version;
      request.result.close();
      resolve(version);
    };
    request.onerror = () => {
      resolve(null);
    };
  });
}

/**
 * Upgrade function - handles migrations from older versions
 */
function handleUpgrade(db: IDBPDatabase<AppDB>, oldVersion: number, newVersion: number) {
  console.log(`IndexedDB upgrade: ${oldVersion} → ${newVersion}`);
  
  // Track upgrade in Sentry
  Sentry.addBreadcrumb({
    message: `IndexedDB upgrade from version ${oldVersion} to ${newVersion}`,
    category: "indexeddb",
    level: "info",
    data: {
      oldVersion,
      newVersion,
      dbName: DB_NAME,
    },
  });

  // Create stores if they don't exist (handles older DB versions)
  if (!db.objectStoreNames.contains("assets")) {
    db.createObjectStore("assets", { keyPath: "id" });
  }
  if (!db.objectStoreNames.contains("inspectionPointsMapping")) {
    db.createObjectStore("inspectionPointsMapping", { keyPath: "tempId" });
  }
  if (!db.objectStoreNames.contains("images")) {
    db.createObjectStore("images", { keyPath: "id" });
  }
  if (!db.objectStoreNames.contains("settings")) {
    db.createObjectStore("settings", { keyPath: "key" });
  }
  
  // Version 6 migration: recreate inboundItems store
  if (oldVersion < 6) {
    if (db.objectStoreNames.contains("inboundItems")) {
      db.deleteObjectStore("inboundItems");
    }
  }
  
  // Create inboundItems store if it doesn't exist
  if (!db.objectStoreNames.contains("inboundItems")) {
    const inboundItemsStore = db.createObjectStore("inboundItems", {
      keyPath: "tempId",
      autoIncrement: true,
    });
    inboundItemsStore.createIndex("by-status", "status_ID");
    inboundItemsStore.createIndex("by-date", "createdAt");
  }
}

async function openConnection(): Promise<IDBPDatabase<AppDB>> {
  try {
    // ✅ Scenario 1: Normal case - DB version is same or lower (upgrade works)
    return await openDB<AppDB>(DB_NAME, CURRENT_DB_VERSION, {
      upgrade: handleUpgrade,
    });
  } catch (error: any) {
    // ✅ Scenario 2: VersionError - DB version is higher than app version
    if (error?.name === "VersionError") {
      console.warn(
        `VersionError: Database version is higher than app version. ` +
        `Attempting to open with existing version...`
      );

      // Get the existing database version
      const existingVersion = await getExistingDbVersion();
      
      if (existingVersion && existingVersion > CURRENT_DB_VERSION) {
        // Database is newer than app - open with existing version
        // This allows the app to work, but schema changes won't be applied
        console.warn(
          `Opening database with existing version ${existingVersion} ` +
          `(app expects ${CURRENT_DB_VERSION}). User may need to update app.`
        );

        // Track this in Sentry - important for monitoring
        Sentry.captureMessage("IndexedDB version mismatch - app is older than database", {
          level: "warning",
          tags: {
            context: "indexeddb",
            error_type: "VersionError",
            action: "version_mismatch",
          },
          extra: {
            appVersion: CURRENT_DB_VERSION,
            dbVersion: existingVersion,
            dbName: DB_NAME,
            message: "User's app version is older than database version. App may need update.",
          },
        });

        // Try to open with existing version (no upgrade function - read existing version)
        // Note: This may fail if schema is incompatible, but it's better than crashing
        try {
          // Open without upgrade function - just read existing version
          return await openDB<AppDB>(DB_NAME, existingVersion);
        } catch (openError: any) {
          // If opening with existing version fails, track and rethrow
          console.error("Failed to open with existing version:", openError);
          
          Sentry.captureException(
            openError instanceof Error ? openError : new Error(String(openError)),
            {
              tags: {
                context: "indexeddb",
                error_type: "VersionError",
                action: "open_existing_version_failed",
              },
              extra: {
                appVersion: CURRENT_DB_VERSION,
                dbVersion: existingVersion,
                dbName: DB_NAME,
                originalError: error?.message,
                openError: openError?.message,
              },
            }
          );

          // Re-throw the error - app will need to handle this gracefully
          throw openError;
        }
      } else {
        // Unexpected VersionError - rethrow
        throw error;
      }
    } else {
      // Other errors - rethrow
      throw error;
    }
  }
}


export async function getDb(): Promise<IDBPDatabase<AppDB>> {
  try {
    if (!dbInstance) {
      dbInstance = await openConnection();
    } else {
      // Quick test to see if it's still alive
      try {
        dbInstance.transaction("assets");
      } catch (testError) {
        // Connection lost - reset and reconnect
        console.warn("IndexedDB connection test failed. Reconnecting...");
        dbInstance = null;
        dbInstance = await openConnection();
      }
    }
    return dbInstance;
  } catch (err: any) {
    console.error("IndexedDB connection error:", err);
    
    // Track connection errors
    if (err?.name === "VersionError") {
      // This should be handled in openConnection, but track it here too
      Sentry.captureException(
        err instanceof Error ? err : new Error(String(err)),
        {
          tags: {
            context: "indexeddb",
            error_type: "VersionError",
            action: "getDb_failed",
          },
          extra: {
            dbName: DB_NAME,
            appVersion: CURRENT_DB_VERSION,
            errorMessage: err?.message,
          },
        }
      );
    } else {
      // Track other connection errors
      Sentry.captureException(
        err instanceof Error ? err : new Error(String(err)),
        {
          tags: {
            context: "indexeddb",
            error_type: err?.name || "Unknown",
            action: "getDb_failed",
          },
          extra: {
            dbName: DB_NAME,
            appVersion: CURRENT_DB_VERSION,
            errorMessage: err?.message,
          },
        }
      );
    }
    
    // Reset instance and try once more
    dbInstance = null;
    try {
      dbInstance = await openConnection();
      return dbInstance;
    } catch (retryError) {
      // Final failure - rethrow
      throw retryError;
    }
  }
}

export async function saveAsset(asset: any) {
  try {
    const db = await getDb();
    await db.put("assets", asset);
  } catch (error: any) {
    console.error("Failed to save asset to IndexedDB:", error);
    captureIndexDBError("saveAsset", error, {
      store: "assets",
      key: asset?.id,
    });
    throw error;
  }
}

export async function getOfflineAssets() {
  const db = await getDb();
  return await db.getAll("assets");
}

export async function getOfflineAssetById(id: number | string) {
  const db = await getDb();
  return await db.get("assets", id);
}

export async function deleteAsset(id: number) {
  const db = await getDb();
  await db.delete("assets", id);
}

export async function updateLocalAsset(id: number, updatedAsset: any) {
  try {
    const db = await getDb();
    await db.put("assets", updatedAsset, id);
  } catch (error: any) {
    console.error("Failed to update local asset:", error);
    captureIndexDBError("updateLocalAsset", error, {
      store: "assets",
      key: id,
    });
    throw error;
  }
}

export async function updateIndexDBSettings(key: string, value: any) {
  try {
    const db = await getDb();
    await db.put("settings", { key: key, value: value });
  } catch (error: any) {
    console.error("Failed to update IndexedDB settings:", error);
    captureIndexDBError("updateIndexDBSettings", error, {
      store: "settings",
      key,
    });
    throw error;
  }
}

export async function getIndexDBSettings(key: string) {
  const db = await getDb();
  const setting = await db.get("settings", key);
  return setting?.value;
}

export async function replaceTempIds(localid: number, serverAsset: any) {
  try {
    const db = await getDb();
    const localAsset = await db.get("assets", localid);

    serverAsset.modules.forEach((serverModule: any, index: number) => {
    const localModule = localAsset.modules.find(
      (m: any) => m.id === `temp-module-${index}`
    );
    if (localModule) {
      serverModule.points.forEach(async (serverPoint: any, pIndex: number) => {
        const localPoint = localModule.points.find(
          (p: any) => p.id === `temp-point-${index}-${pIndex}`
        );
        if (localPoint) {
          await db.put("inspectionPointsMapping", {
            tempId: localPoint.id,
            realId: serverPoint.id,
          });
          localPoint.id = serverPoint.id; // Replace temp point ID
        }
      }); // Replace temp module ID
    }
  });

    return serverAsset;
  } catch (error: any) {
    console.error("Failed to replace temp IDs:", error);
    captureIndexDBError("replaceTempIds", error, {
      store: "assets",
      key: localid,
    });
    throw error;
  }
}

export async function getInspectionPointMapping(
  localId: number | string
): Promise<number> {
  try {
    const db = await getDb();
    const mapping = await db.get("inspectionPointsMapping", localId as number);
    return mapping ? mapping.realId : -1;
  } catch (err: any) {
    console.error("IndexedDB failure in getInspectionPointMapping:", err);
    captureIndexDBError("getInspectionPointMapping", err, {
      store: "inspectionPointsMapping",
      key: localId,
    });
    throw new Error("indexeddb-lost-connection");
  }
}

export async function saveInspectionPointMapping(
  localId: number | string,
  realId: number
) {
  try {
    const db = await getDb();
    await db.put("inspectionPointsMapping", { tempId: localId, realId: realId });
  } catch (error: any) {
    console.error("Failed to save inspection point mapping:", error);
    captureIndexDBError("saveInspectionPointMapping", error, {
      store: "inspectionPointsMapping",
      key: localId,
      realId,
    });
    throw error;
  }
}

export async function putImage(id: string, blob: Blob) {
  try {
    const db = await getDb();
    await db.put("images", { id: id, blob: blob });
  } catch (e: any) {
    console.error("unable to store image in IDB:", e);
    captureIndexDBError("putImage", e, {
      store: "images",
      key: id,
      blobSize: blob.size,
    });
    throw e;
  }
}

export async function getImage(id: string) {
  try {
    const db = await getDb();
    return await db.get("images", id);
  } catch (e: any) {
    console.error("IndexedDB failure in images:", e);
    captureIndexDBError("getImage", e, {
      store: "images",
      key: id,
    });
    return undefined;
  }
}

export async function deleteImage(id: string) {
  try {
    const db = await getDb();
    await db.delete("images", id);
  } catch (e) {
    console.error(`Failed to delete image with ID ${id} from IndexedDB:`, e);
  }
}

export async function getAllImages() {
  try {
    const db = await getDb();
    return await db.getAll("images");
  } catch (e) {
    console.error("Failed to get all images from IndexedDB:", e);
    return [];
  }
}

export async function putThumbnail(id: string, blob: Blob) {
  try {
    const db = await getDb();
    await db.put("images", { id: `${id}_thumb`, blob: blob });
  } catch (e: any) {
    console.error("unable to store thumbnail in IDB:", e);
    captureIndexDBError("putThumbnail", e, {
      store: "images",
      key: `${id}_thumb`,
      blobSize: blob.size,
    });
    throw e;
  }
}

export async function getThumbnail(id: string) {
  try {
    const db = await getDb();
    return await db.get("images", `${id}_thumb`);
  } catch (e: any) {
    console.error("IndexedDB failure in thumbnails:", e);
    captureIndexDBError("getThumbnail", e, {
      store: "images",
      key: `${id}_thumb`,
    });
    return undefined;
  }
}